import { AuthEventBus } from './AuthEventBus';
import { OidcClient } from '@axa-fr/oidc-client';
import type { KeycloakConfig } from '@thinkalpha/ui-types/types/schemas/OAuthConfigSchema';
import { injectable, inject } from 'src/features/ioc';
import type {
    ManagedOAuth2Service,
    OAuthFlowState,
    LoginCallbackResult,
    AuthEventCallback,
    EnsureValidSessionResult,
} from 'src/services/OAuth2Service';
import type { OAuth2StorageService } from 'src/services/OAuth2StorageService';
import type { OidcLocationService } from 'src/services/OidcLocationService';
import type { SyncStorageService } from 'src/services/SyncStorageService';
import { container as staticContainer } from 'src/StaticContainer';
import type { IsomorphicBindings } from 'src/types/bindings';

const SESSION_RESTORED_KEY = 'oidc_session_restored';

@injectable()
export class OidcClientOAuth2ServiceImpl implements ManagedOAuth2Service {
    type = 'managed' as const;

    #authEventBus: AuthEventBus;
    #config: KeycloakConfig;
    #log: IsomorphicBindings['Logger'];
    #oidcClient: OidcClient | null = null;
    #oidcLocation: OidcLocationService;
    #syncStorage: SyncStorageService;
    #tokenRefreshRetryCount = 0;

    // TODO: Make these configurable
    #maxRetryCount = 3;
    #retryDelayMs = 1000;

    constructor(
        @inject('ConfigService') protected readonly config: IsomorphicBindings['ConfigService'],
        @inject('Logger') protected readonly log: IsomorphicBindings['Logger'],
        @inject('OAuth2StorageService') protected readonly oAuth2Storage: OAuth2StorageService,
        @inject('OidcLocationService') oidcLocation: OidcLocationService,
        @inject('SyncStorageService') syncStorage: SyncStorageService,
    ) {
        this.#log = log.getSubLogger(
            { name: 'features:oauth2:lib:OidcClientOAuth2ServiceImpl' },
            { component: 'auth' },
        );
        this.#config = config.oAuthConfig as KeycloakConfig;
        this.#authEventBus = new AuthEventBus();
        this.#oidcLocation = oidcLocation;
        this.#syncStorage = syncStorage;

        this.#initializeOidcClient();
        this.#startSessionRestorationMonitor();
    }

    #initializeOidcClient(): void {
        const { keycloakURL, tenantID, applicationID, callbackURL } = this.#config;

        // Build the authority URL from Keycloak config
        const authority = new URL(`${tenantID}`, keycloakURL).toString();

        // Construct silent callback URL - must be on same origin as main app
        const silentCallbackUrl = new URL('/silent-callback.html', callbackURL).toString();

        // TODO: Make scope configurable
        const configuration = {
            client_id: applicationID,
            redirect_uri: callbackURL,
            authority,
            scope: 'openid profile offline_access', // offline_access for refresh tokens
            // Use injectable storage instead of sessionStorage (default) so tokens are shared
            // across tabs. This ensures logout clears tokens for all tabs immediately.
            // On web this wraps localStorage; on native it uses file-based Persisted storage.
            storage: this.#syncStorage as Storage,
            // TODO: Make these configurable
            response_type: 'code',
            loadUserInfo: false, // We handle userInfo ourselves

            // Automatic silent token renewal configuration
            // When enabled, the OIDC client will automatically refresh tokens before they expire
            // using a hidden iframe that loads silent_redirect_uri.
            // Disabled in Node.js (Electron main process) where there is no DOM/iframe support.
            automaticSilentRenew: typeof document !== 'undefined',
            silent_redirect_uri: silentCallbackUrl,
            // Refresh tokens before they expire to prevent gaps in authorization
            // TODO: Make configurable. Using 15s for testing with short-lived tokens.
            refresh_time_before_tokens_expiration_in_second: 15,
            // Trigger renewal when access token or ID token becomes invalid
            token_renew_mode: 'access_token_or_id_token_invalid',

            // Keep explicit login flow (no automatic silent signin on app load)
            automaticSilentSignin: false,

            // Revoke tokens server-side during logout to prevent other tabs from
            // using their cached refresh tokens to create new sessions
            logout_tokens_to_invalidate: ['access_token', 'refresh_token'] as ('access_token' | 'refresh_token')[],
        };

        if (TRACE) {
            this.#log.trace('Initializing OIDC client with configuration', { configuration });
        }

        this.#oidcClient = OidcClient.getOrCreate(() => fetch, this.#oidcLocation)(configuration);

        // Subscribe to OIDC client events
        this.#oidcClient.subscribeEvents((eventName: string, data: any) => {
            this.#handleOidcEvent(eventName, data);
        });
    }

    #emitSessionExpired(): void {
        this.#authEventBus.emit({ type: 'SESSION_EXPIRED' });
        try {
            staticContainer.getOptional('AnalyticsService')?.track({ type: 'session_expired' });
        } catch {
            // Analytics should never break the app
        }
    }

    #handleOidcEvent(eventName: string, data: any): void {
        switch (eventName) {
            case 'token_timer':
            case 'refreshTokensAsync_begin':
            case 'refreshTokensAsync_end':
            case 'tryKeepExistingSessionAsync_begin':
            case 'tryKeepExistingSessionAsync_end':
                // Internal OIDC client events - ignore silently
                break;

            case 'token_renewed': {
                this.#tokenRefreshRetryCount = 0; // Reset retry count on success
                // Read tokens from the OIDC client's canonical source, not from event data
                // The event data structure varies, but this.#oidcClient.tokens is always up-to-date
                const tokens = this.#oidcClient?.tokens;
                const accessToken = tokens?.accessToken;
                if (TRACE) {
                    this.#log.trace('token_renewed: Reading tokens from OIDC client', {
                        hasOidcClient: !!this.#oidcClient,
                        hasTokens: !!tokens,
                        accessTokenLength: accessToken?.length ?? 0,
                        eventDataKeys: Object.keys(data || {}),
                    });
                }
                if (accessToken) {
                    this.#authEventBus.emit({
                        type: 'TOKEN_REFRESHED',
                        payload: {
                            tokens: {
                                accessToken,
                                idToken: tokens?.idToken,
                                refreshToken: tokens?.refreshToken,
                            },
                        },
                    });
                } else {
                    this.#log.warn('token_renewed event received but no access token available');
                }
                break;
            }

            case 'token_expired':
                this.#emitSessionExpired();
                break;

            case 'session_terminated':
                this.#authEventBus.emit({ type: 'LOGOUT' });
                break;

            default:
                if (TRACE) {
                    this.#log.trace('Unhandled OIDC event', { eventName, data });
                }
        }
    }

    #startSessionRestorationMonitor(): void {
        if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
            window.addEventListener('storage', (e) => {
                if (e.key === SESSION_RESTORED_KEY && e.newValue) {
                    this.#checkTokenValid().then((result) => {
                        if (result.success) {
                            this.#authEventBus.emit({ type: 'SESSION_RESTORED' });
                        }
                    });
                }
            });
        }
    }

    async #checkTokenValid(): Promise<{ success: boolean; token?: string }> {
        try {
            if (!this.#oidcClient) {
                return { success: false };
            }

            const token = await this.#oidcClient.getValidTokenAsync();
            return { success: true, token: (token as any).access_token || (token as any).accessToken || String(token) };
        } catch (error) {
            if (TRACE) {
                this.#log.trace('Token validation failed', { error });
            }
            return { success: false };
        }
    }

    /**
     * Check whether an access token JWT is expired (or will expire within a
     * small grace window). Returns false for non-JWT tokens or tokens without
     * an `exp` claim so they proceed to server-side validation.
     */
    #isAccessTokenExpired(accessToken: string): boolean {
        try {
            const parts = accessToken.split('.');
            if (parts.length !== 3) return false; // Not a JWT
            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const payload: { exp?: number } = JSON.parse(atob(base64));
            if (typeof payload.exp !== 'number') return false;
            // 5-second grace window to avoid edge-case races between check and use
            return payload.exp * 1000 <= Date.now() + 5000;
        } catch {
            return false;
        }
    }

    /**
     * Remove only OIDC login flow state keys from sync storage, preserving
     * token storage and any non-OIDC data. These are the keys that can become
     * stale and cause "State not valid" errors on subsequent login attempts.
     */
    #clearOidcLoginFlowKeys(): void {
        const loginFlowPrefixes = [
            'oidc.state.',
            'oidc.nonce.',
            'oidc.code_verifier.',
            'oidc.login.',
            'oidc.session_state.',
        ];

        const keysToRemove: string[] = [];
        for (let i = 0; i < this.#syncStorage.length; i++) {
            const key = this.#syncStorage.key(i);
            if (key && loginFlowPrefixes.some((prefix) => key.startsWith(prefix))) {
                keysToRemove.push(key);
            }
        }

        for (const key of keysToRemove) {
            this.#syncStorage.removeItem(key);
        }

        if (TRACE) {
            this.#log.trace('Cleared OIDC login flow keys', { removedKeys: keysToRemove });
        }
    }

    async startOauth(state: OAuthFlowState = {}): Promise<void> {
        if (TRACE) {
            this.#log.trace('Starting OAuth flow', { state });
        }

        if (!this.#oidcClient) {
            throw new Error('OIDC client not initialized');
        }

        // Check if a login flow is already in progress
        if (this.oAuth2Storage.isLoginFlowInProgress()) {
            this.#log.warn('Attempt to start multiple OAuth flows - ignoring duplicate request', { state });
            return;
        }

        // Store the OAuth state
        await this.oAuth2Storage.storeOAuthState(state);

        try {
            staticContainer.getOptional('AnalyticsService')?.track({ type: 'login_initiated' });
        } catch {
            // Analytics should never break the app
        }

        try {
            await this.#oidcClient.loginAsync(undefined, state.extras);
        } catch (error) {
            this.#log.error('Failed to start OAuth flow', { error, state });
            throw error;
        }
    }

    async loginCallback(queryString: string): Promise<LoginCallbackResult> {
        if (TRACE) {
            this.#log.trace('Processing login callback', { queryString });
        }

        if (!this.#oidcClient) {
            throw new Error('OIDC client not initialized');
        }

        try {
            const callbackResult = await this.#oidcClient.loginCallbackAsync();
            const state = await this.oAuth2Storage.getOAuthState();

            const tokens = this.#oidcClient.tokens;

            if (TRACE) {
                this.#log.trace('Login callback successful', { callbackResult, tokens });
            }

            try {
                staticContainer.getOptional('AnalyticsService')?.track({ type: 'login_success' });
            } catch {
                // Analytics should never break the app
            }

            return {
                state,
                tokens: {
                    accessToken: tokens.accessToken,
                    idToken: tokens.idToken,
                    refreshToken: tokens.refreshToken,
                },
            };
        } catch (error) {
            // Detect OAuth state mismatch - this happens when sync-storage contains
            // stale OIDC state from a previous session. Clear the corrupted state so
            // a retry of the login flow can succeed.
            if (error instanceof Error && error.message.includes('State not valid')) {
                this.#log.warn('OAuth state mismatch detected - clearing stale auth state from storage');
                try {
                    this.#clearOidcLoginFlowKeys();
                    await this.oAuth2Storage.clearOAuthData();
                } catch (cleanupError) {
                    this.#log.error('Failed to clear stale auth state during state mismatch recovery', {
                        error: cleanupError,
                    });
                }
            }

            this.#log.error('Login callback failed', { error, queryString });
            try {
                staticContainer.getOptional('AnalyticsService')?.track({
                    type: 'login_failure',
                    error: error instanceof Error ? error.message : String(error),
                });
            } catch {
                // Analytics should never break the app
            }
            throw error;
        }
    }

    async logout(options?: { noRedirect?: boolean }): Promise<void> {
        if (TRACE) {
            this.#log.trace('Starting logout', { options });
        }

        if (!this.#oidcClient) {
            this.#log.warn('OIDC client not initialized during logout');
            return;
        }

        try {
            // Check if we have tokens before calling logoutAsync.
            // If tokens were already cleared (e.g., by another tab logging out),
            // the OIDC client's logoutAsync would use an empty id_token_hint=""
            // (fallback value when e.tokens is null), which Keycloak rejects.
            // In this case, we skip the OIDC logout and redirect directly.
            const hasTokens = Boolean(this.#oidcClient.tokens?.idToken);

            if (hasTokens) {
                // Build extras for logoutAsync
                // The library checks for "no_reload:oidc" === "true" to skip the redirect
                const extras = options?.noRedirect ? { 'no_reload:oidc': 'true' } : undefined;

                await this.#oidcClient.logoutAsync(this.#config.loggedOutURL, extras);
            } else {
                this.#log.info('No tokens available (possibly cleared by another tab), skipping OIDC logout');
                // Only redirect if not suppressed
                if (!options?.noRedirect) {
                    this.#oidcLocation.open(this.#config.loggedOutURL);
                }
            }

            // Clear stored OAuth data
            await this.oAuth2Storage.clearOAuthData();

            this.#log.info('Logout completed successfully');
        } catch (error) {
            this.#log.error('Logout failed', { error });
            throw error;
        }
    }

    async getValidToken(): Promise<string> {
        if (!this.#oidcClient) {
            throw new Error('OIDC client not initialized');
        }

        try {
            const validToken = await this.#oidcClient.getValidTokenAsync();
            return (validToken as any).access_token || (validToken as any).accessToken || String(validToken);
        } catch (error) {
            this.#log.error('Failed to get valid token', { error });
            throw error;
        }
    }

    async handleUnauthorized(): Promise<{ success: boolean; token?: string }> {
        if (TRACE) {
            this.#log.trace('Handling unauthorized response, attempting token refresh');
        }

        if (!this.#oidcClient) {
            this.#log.error('OIDC client not initialized during unauthorized handling');
            return { success: false };
        }

        // Check if we've exceeded retry limit
        if (this.#tokenRefreshRetryCount >= this.#maxRetryCount) {
            this.#log.warn('Token refresh retry limit exceeded', { retryCount: this.#tokenRefreshRetryCount });
            this.#emitSessionExpired();
            return { success: false };
        }

        try {
            this.#tokenRefreshRetryCount++;

            // Add delay for retries to prevent rapid succession
            if (this.#tokenRefreshRetryCount > 1) {
                await new Promise((resolve) => setTimeout(resolve, this.#retryDelayMs));
            }

            // Attempt token refresh
            await this.#oidcClient.renewTokensAsync();

            // Get the refreshed token
            const validToken = await this.#oidcClient.getValidTokenAsync();

            this.#log.info('Token refresh successful', { retryCount: this.#tokenRefreshRetryCount });

            // Signal storage event for cross-tab synchronization
            if (typeof window !== 'undefined') {
                this.#syncStorage.setItem(SESSION_RESTORED_KEY, Date.now().toString());
                this.#syncStorage.removeItem(SESSION_RESTORED_KEY);
            }

            return {
                success: true,
                token: (validToken as any).access_token || (validToken as any).accessToken || String(validToken),
            };
        } catch (error) {
            this.#log.warn('Token refresh failed', {
                error,
                retryCount: this.#tokenRefreshRetryCount,
                maxRetries: this.#maxRetryCount,
            });

            // If we've exhausted retries, emit session expired
            if (this.#tokenRefreshRetryCount >= this.#maxRetryCount) {
                this.#emitSessionExpired();
            }

            return { success: false };
        }
    }

    async ensureValidSession(): Promise<EnsureValidSessionResult> {
        if (TRACE) {
            this.#log.trace('ensureValidSession: Checking session validity on app load');
        }

        if (!this.#oidcClient) {
            this.#log.error('ensureValidSession: OIDC client not initialized');
            this.#emitSessionExpired();
            return { isValid: false };
        }

        try {
            // First, try to keep an existing session (checks if we have valid stored tokens)
            const hasExistingSession = await this.#oidcClient.tryKeepExistingSessionAsync();

            if (TRACE) {
                this.#log.trace('ensureValidSession: tryKeepExistingSessionAsync result', { hasExistingSession });
            }

            if (hasExistingSession) {
                const tokens = this.#oidcClient.tokens;

                // tryKeepExistingSessionAsync returns true whenever tokens exist in storage,
                // even if they are expired. Verify the access token's JWT `exp` claim before
                // emitting TOKEN_REFRESHED — otherwise downstream consumers (e.g. userInfo
                // check in setAccessTokenSaga) will use an expired token, get a 401, and
                // trigger a spurious logout cascade.
                if (tokens?.accessToken && this.#isAccessTokenExpired(tokens.accessToken)) {
                    this.#log.info(
                        'ensureValidSession: Stored access token is expired, attempting renewal instead of restoring',
                    );
                    // Fall through to the renewal path below
                } else {
                    const result: EnsureValidSessionResult = {
                        isValid: true,
                        tokens: {
                            accessToken: tokens.accessToken,
                            idToken: tokens.idToken,
                            refreshToken: tokens.refreshToken,
                        },
                    };

                    this.#log.info('ensureValidSession: Existing session restored successfully');

                    // Emit TOKEN_REFRESHED so Redux state is updated
                    this.#authEventBus.emit({
                        type: 'TOKEN_REFRESHED',
                        payload: { tokens: result.tokens },
                    });

                    return result;
                }
            }

            // No valid existing session (or stored tokens were expired), try to refresh
            if (TRACE) {
                this.#log.trace('ensureValidSession: No existing session, attempting refresh');
            }

            try {
                await this.#oidcClient.renewTokensAsync();

                const tokens = this.#oidcClient.tokens;
                const result: EnsureValidSessionResult = {
                    isValid: true,
                    tokens: {
                        accessToken: tokens.accessToken,
                        idToken: tokens.idToken,
                        refreshToken: tokens.refreshToken,
                    },
                };

                this.#log.info('ensureValidSession: Session refreshed successfully');

                // Emit TOKEN_REFRESHED so Redux state is updated
                this.#authEventBus.emit({
                    type: 'TOKEN_REFRESHED',
                    payload: { tokens: result.tokens },
                });

                return result;
            } catch (refreshError) {
                if (TRACE) {
                    this.#log.trace('ensureValidSession: Refresh failed', { refreshError });
                }

                // Refresh failed, session is expired
                this.#log.info('ensureValidSession: Session expired, user needs to re-authenticate');
                this.#emitSessionExpired();
                return { isValid: false };
            }
        } catch (error) {
            this.#log.error('ensureValidSession: Unexpected error', { error });
            this.#emitSessionExpired();
            return { isValid: false };
        }
    }

    subscribeToAuthEvents(callback: AuthEventCallback): () => void {
        return this.#authEventBus.subscribe(callback);
    }
}
